import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import type { JSX } from "solid-js";
import { createSignal, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import {
  IconContact,
  IconHandshake,
  IconInfo,
  IconLink,
  IconPencil,
  IconTrash,
} from "~/components/icons";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import {
  businessDetailResource,
  businessesResource,
} from "~/features/business/business.resource";
import { getVendorDetailPageData } from "~/features/vendor/vendor-detail-page";
import { deleteVendor } from "~/features/vendor/vendor.api";
import { useVendorForm } from "~/features/vendor/vendor.form";
import { VendorDetailResource } from "~/features/vendor/vendor.resource";
import type { VendorItem } from "~/features/vendor/vendor.server";
import { withBusinessGuard } from "~/lib/route-guards";
import { formatDisplayCalendarDate } from "~/lib/utils";

export const Route = createFileRoute("/_dashboard/dashboard/vendors/$vendorId")(
  {
    staticData: {
      breadcrumb: (m) => m.loaderData?.vendor?.name ?? "Vendor",
    },
    validateSearch: z.object({
      mode: z.enum(["edit"]).optional(),
    }),
    // Route-specific 404 message (takes precedence over the dashboard-wide
    // notFoundComponent). Shown when a vendor doesn't exist — e.g. it was
    // deleted, or an AI agent linked here by mistake.
    notFoundComponent: () => (
      <NotFound
        title="Vendor not found"
        message="This vendor doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
        homeTo="/dashboard/vendors"
        homeLabel="Go to vendors"
      />
    ),
    ...withBusinessGuard(),
    loader: async ({ params, context }) => {
      const collection = await context.queryClient.ensureQueryData(
        businessesResource.options(undefined),
      );
      const businessId = collection.activeBusinessId!;

      if (params.vendorId === "new") {
        const data = await getVendorDetailPageData({
          data: { businessId, vendorId: "new" },
        });
        businessDetailResource.seed(
          context.queryClient,
          businessId,
          data.businessDetail,
        );
        context.queryClient.setQueryData(
          VendorDetailResource.key({ businessId, id: "new" }),
          null,
        );
        return {
          businessId,
          activeBusiness: data.businessDetail,
          vendor: null,
        };
      }

      const data = await getVendorDetailPageData({
        data: { businessId, vendorId: params.vendorId },
      });
      businessDetailResource.seed(
        context.queryClient,
        businessId,
        data.businessDetail,
      );
      if (data.vendor) {
        VendorDetailResource.seed(
          context.queryClient,
          { businessId, id: params.vendorId },
          data.vendor,
        );
      }
      // Prefetch the Associations panel queries so they resolve from cache on
      // mount instead of flashing loading states. Fire-and-forget (not awaited)
      // so it never blocks the loader's critical path or SSR.
      prefetchAssociations(
        context.queryClient,
        businessId,
        "vendor",
        params.vendorId,
      );
      return {
        businessId,
        activeBusiness: data.businessDetail,
        vendor: data.vendor,
      };
    },
    component: VendorDetailPage,
  },
);

type Mode = "view" | "edit";

function VendorDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  if (data().vendor) {
    VendorDetailResource.seedIfEmpty(
      qc,
      { businessId: data().businessId, id: params().vendorId },
      data().vendor!,
    );
  } else if (params().vendorId === "new") {
    // Seed null into QueryClient for "new" to prevent SSR deferred stream hang
    const newKey = VendorDetailResource.key({
      businessId: data().businessId,
      id: params().vendorId,
    });
    if (qc.getQueryData(newKey) === undefined) {
      qc.setQueryData(newKey, null);
    }
  }

  const isNew = () => params().vendorId === "new";
  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const vendorQuery = createQuery(() => ({
    ...VendorDetailResource.options(
      { businessId: data().businessId, id: params().vendorId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  return (
    <div
      class={`flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10 ${
        !isNew() && mode() === "view"
          ? "@5xl/main:h-[calc(100svh-4.5rem)] @5xl/main:max-h-[calc(100svh-4.5rem)] @5xl/main:min-h-0 @5xl/main:overflow-auto"
          : ""
      }`}
    >
      <Show when={isNew()}>
        <VendorEdit
          vendor={null}
          businessId={data().businessId}
          onCancel={() => navigate({ to: "/dashboard/vendors" })}
          onSaved={() => navigate({ to: "/dashboard/vendors" })}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={vendorQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(vendor) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <VendorView
                  vendor={vendor()}
                  businessId={data().businessId}
                  setMode={setMode}
                  onDeleted={() => {
                    VendorDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().vendorId,
                    });
                    // Remove the list cache so the table doesn't flash the
                    // deleted row before a background refetch.
                    qc.removeQueries({
                      queryKey: ["vendor", data().businessId],
                    });
                    navigate({ to: "/dashboard/vendors" });
                  }}
                />
              }
            >
              <VendorEdit
                vendor={vendor()}
                businessId={data().businessId}
                onCancel={() => setMode("view")}
                onSaved={() => {
                  VendorDetailResource.invalidate(qc, {
                    businessId: data().businessId,
                    id: params().vendorId,
                  });
                  setMode("view");
                }}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteVendorDialog(props: {
  vendor: VendorItem;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteVendor({
        data: { id: props.vendor.id, businessId: props.businessId },
      });
      props.onDeleted();
    } catch {
      toast.error("Failed to delete vendor");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Vendor</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">"{props.vendor.name}"</span>. This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- View Mode ----------------------------------------------------------------

function VendorView(props: {
  vendor: VendorItem;
  businessId: string;
  setMode: (mode: Mode) => void;
  onDeleted: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = createSignal(false);

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconHandshake class="size-5" />}
        title={props.vendor.name}
        badge="Vendor"
        date={props.vendor.createdAt ?? undefined}
        menuContent={
          <>
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              <StandardRouteDetailCard
                icon={<IconContact class="size-5" />}
                title="Vendor Details"
                contentClass="divide-y divide-border"
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit vendor"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                <InfoRow label="Name" value={props.vendor.name} />
                <InfoRow label="Contact" value={props.vendor.contactName} />
                <InfoRow label="Email" value={props.vendor.email} />
                <InfoRow label="Phone" value={props.vendor.phone} />
                <InfoRow label="Website" value={props.vendor.website} />
                <InfoRow
                  label="Address"
                  value={formatVendorAddress(props.vendor.address)}
                  multiline
                />
                <InfoRow label="Notes" value={props.vendor.notes} multiline />
              </StandardRouteDetailCard>
            </div>
          </StandardRouteDetailLeftColumn>
          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="record"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="record" class="flex-1">
                      <IconInfo class="mr-2 size-4" />
                      <span class="hidden sm:inline">Record Info</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="record"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <div class="divide-y divide-border">
                          <InfoRow
                            label="Created"
                            value={formatDisplayCalendarDate(
                              new Date(props.vendor.createdAt),
                            )}
                          />
                          <InfoRow
                            label="Updated"
                            value={formatDisplayCalendarDate(
                              new Date(props.vendor.updatedAt),
                            )}
                          />
                          <InfoRow
                            label="Business"
                            value="Active business vendor record"
                          />
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{ type: "vendor", id: props.vendor.id }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "bank-account",
                              defaultVisibility: {
                                bankName: false,
                                accountCode: false,
                                description: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "bank-transaction",
                              defaultVisibility: {
                                status: false,
                                institutionName: false,
                                mappedAccountCode: false,
                                bankAccountName: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "journal-entry",
                              defaultVisibility: {
                                reference: false,
                                entries: false,
                              },
                            },
                            {
                              type: "document",
                              defaultVisibility: {
                                type: false,
                                amount: false,
                              },
                            },
                            {
                              type: "fixed-asset",
                              defaultVisibility: {
                                purchaseDate: false,
                                depreciationMethod: false,
                                purchaseCost: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "worksheet",
                              defaultVisibility: {
                                description: false,
                                type: false,
                                updatedAt: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteVendorDialog
        vendor={props.vendor}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- Edit / Create Form -------------------------------------------------------

function VendorEdit(props: {
  vendor: VendorItem | null;
  businessId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = () => props.vendor === null;
  const [serverError, setServerError] = createSignal("");

  const form = useVendorForm(props.businessId, props.vendor, {
    onSuccess: () => {
      toast.success(
        isNew() ? "Vendor created successfully" : "Vendor updated successfully",
      );
      props.onSaved();
    },
    onError: (e) =>
      setServerError(
        e instanceof Error ? e.message : "An unexpected error occurred",
      ),
  });

  return (
    <StandardRouteDetailEditCard
      title={isNew() ? "Add Vendor" : "Edit Vendor"}
      description={
        isNew()
          ? "Create a new vendor record."
          : "Update the vendor details. Changes apply immediately on save."
      }
      formId="vendor-edit-form"
      onCancel={props.onCancel}
      saveButton={
        <form.Subscribe>
          {(state) => (
            <Button
              type="submit"
              form="vendor-edit-form"
              disabled={state().isSubmitting}
            >
              {state().isSubmitting
                ? "Saving..."
                : isNew()
                  ? "Add Vendor"
                  : "Save Changes"}
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <form
        id="vendor-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
          <Col span={1} spanLg={2}>
            <form.Field name="name">
              {(field) => (
                <FormField label="Name" required field={field()}>
                  <TextFieldInput
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Vendor name"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="contactName">
              {(field) => (
                <FormField label="Contact Name" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Primary contact"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={2}>
            <form.Field name="email">
              {(field) => (
                <FormField label="Email" field={field()}>
                  <TextFieldInput
                    type="email"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="vendor@example.com"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="phone">
              {(field) => (
                <FormField label="Phone" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="+1 (555) 000-0000"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={2}>
            <form.Field name="website">
              {(field) => (
                <FormField label="Website" field={field()}>
                  <TextFieldInput
                    type="url"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="https://example.com"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="address.line1">
              {(field) => (
                <FormField label="Address Line 1" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Street address"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="address.line2">
              {(field) => (
                <FormField label="Address Line 2" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Apt, suite, unit..."
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="address.city">
              {(field) => (
                <FormField label="City" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="City"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="address.state">
              {(field) => (
                <FormField label="State" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="State"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="address.postalCode">
              {(field) => (
                <FormField label="Postal Code" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Postal Code"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="address.country">
              {(field) => (
                <FormField label="Country" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Country"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="notes">
              {(field) => (
                <FormField label="Notes" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Any additional notes..."
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
        </Grid>

        <Show when={serverError()}>
          <p class="mt-4 text-sm text-destructive">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

function FormField(props: {
  label: string;
  required?: boolean;
  field: {
    state: { meta: { errors: (string | { message: string } | undefined)[] } };
  };
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}

function formatVendorAddress(address: VendorItem["address"]): string | null {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(", "),
    address.postalCode,
    address.country,
  ].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join("\n") : null;
}

function InfoRow(props: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  if (!props.value) return null;

  if (props.label === "Email") {
    return (
      <div class="flex items-center justify-between py-3.5 gap-4">
        <p class="text-sm text-muted-foreground">{props.label}</p>
        <a
          href={`mailto:${props.value}`}
          class="text-sm font-semibold text-primary hover:underline"
        >
          {props.value}
        </a>
      </div>
    );
  }

  if (props.label === "Website") {
    const href = props.value.startsWith("http")
      ? props.value
      : `https://${props.value}`;
    return (
      <div class="flex items-center justify-between py-3.5 gap-4">
        <p class="text-sm text-muted-foreground">{props.label}</p>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm font-semibold text-primary hover:underline"
        >
          {props.value.replace(/^https?:\/\//, "")}
        </a>
      </div>
    );
  }

  return (
    <div
      class={
        props.multiline
          ? "py-3.5 space-y-1.5"
          : "flex items-center justify-between py-3.5 gap-4"
      }
    >
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6
        class={
          props.multiline
            ? "text-sm font-medium whitespace-pre-wrap"
            : "text-sm font-semibold text-right"
        }
      >
        {props.value}
      </h6>
    </div>
  );
}
